import { Injectable } from '@angular/core';

export type SearchOperation =
  | 'GREATER_THAN'
  | 'LESS_THAN'
  | 'GREATER_THAN_EQUAL'
  | 'LESS_THAN_EQUAL'
  | 'NOT_EQUAL'
  | 'EQUAL'
  | 'MATCH'
  | 'MATCH_START'
  | 'MATCH_END'
  | 'IN'
  | 'NOT_IN';

export type LinkOperator = 'AND' | 'OR';

@Injectable({ providedIn: 'root' })
export class FilterSearchByTermService {
  buildSearch(
    searchTerm: string | undefined,
    fields: string[],
    operation: SearchOperation = 'MATCH',
    linkOperator: LinkOperator = 'OR',
  ) {
    if (!searchTerm?.trim() || fields.length === 0) return [];

    return fields.map((field, index) => ({
      key: field,
      operation: operation,
      value: operation === 'IN' ? searchTerm.split(',') : searchTerm.trim(),
      linkOperator: index < fields.length - 1 ? linkOperator : undefined,
    }));
  }

  buildExactSearch(
    searches: {
      field: string;
      value: any;
      operation?: SearchOperation;
      linkOperator?: LinkOperator;
    }[],
  ) {
    if (!searches.length) return [];

    return searches.map((search, index) => ({
      key: search.field,
      operation: search.operation || 'EQUAL',
      value: search.value,
      linkOperator: index < searches.length - 1 ? search.linkOperator || 'AND' : undefined,
    }));
  }

  buildOrder(sortColumn: string | null | undefined, sortDirection = 'ASC') {
    if (!sortColumn) return [];

    return [
      {
        column: sortColumn,
        direction: sortDirection,
      },
    ];
  }

  build(
    page: number,
    size: number,
    options: {
      search?: string;
      fields?: string[];
      operation?: SearchOperation;
      linkOperator?: LinkOperator;
      sort?: string | null;
      direction?: 'ASC' | 'DESC';
    },
  ) {
    const filters: any = { page, size };

    const searchConditions = this.buildSearch(
      options.search,
      options.fields || [],
      options.operation || 'MATCH',
      options.linkOperator || 'OR',
    );
    if (searchConditions.length) {
      filters.searchsBy = searchConditions;
    }

    const orderConditions = this.buildOrder(options.sort, options.direction);
    if (orderConditions.length) {
      filters.ordersBy = orderConditions;
    }

    return filters;
  }

  buildAdvanced(
    page: number,
    size: number,
    options: {
      searchConditions?: Array<{
        field: string;
        value: any;
        operation?: SearchOperation;
        linkOperator?: LinkOperator;
      }>;
      orderBy?: { column: string; direction?: 'ASC' | 'DESC' };
    },
  ) {
    const filters: any = { page, size };

    if (options.searchConditions?.length) {
      filters.searchsBy = options.searchConditions.map((cond, index) => ({
        key: cond.field,
        operation: cond.operation || 'EQUAL',
        value: cond.value,
        linkOperator:
          index < options.searchConditions!.length - 1 ? cond.linkOperator || 'AND' : undefined,
      }));
    }

    if (options.orderBy) {
      filters.ordersBy = [
        {
          column: options.orderBy.column,
          direction: options.orderBy.direction || 'ASC',
        },
      ];
    }

    return filters;
  }
}
